import {FC, useEffect, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import Swal from 'sweetalert2'
import axiosInstance from './api/axiosInstance'

interface LocationState {
  id: string
  origin: string
  destination: string
  destinationSelectedParams: string // Ini yang dari API rute
  selectedDestinationName: string // Ini yang kita tambahkan di navigate
  schDate: string
  originAddress?: string
  std: string
  sta: string
  estimasi: string
  route: string
}

const PegadaianCheckoutPage: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as LocationState | null

  const [showQuotaModal, setShowQuotaModal] = useState(false)
  const [isQuotaFull, setIsQuotaFull] = useState(true) // Set true untuk disable input

  useEffect(() => {
    const timer = setTimeout(() => setShowQuotaModal(true), 500)
    return () => clearTimeout(timer)
  }, [])

  const [openDataDiri, setOpenDataDiri] = useState(true)
  const [jumlahPeserta, setJumlahPeserta] = useState(1)
  const [openDataMudik, setOpenDataMudik] = useState(false)
  const [isNasabah, setIsNasabah] = useState(false)
  const [openUpload, setOpenUpload] = useState(false)
  const [ktpFileName, setKtpFileName] = useState('')
  const [ktpFile, setKtpFile] = useState<File | null>(null)
  const [captchaValue, setCaptchaValue] = useState('')

  const generateCaptcha = () => {
    return Math.floor(10000 + Math.random() * 90000).toString()
  }

  const [captcha, setCaptcha] = useState(generateCaptcha())

  const [formPendaftar, setFormPendaftar] = useState({
    nik: '',
    noKK: '',
    nama: '',
    jenisKelamin: 'L',
    tanggalLahir: '',
    alamatLengkap: '',
    hp: '',
    email: '',
    nomorCif: '',
  })

  const [passengers, setPassengers] = useState([
    {nama: '', nik: '', phone: '', relation: ''},
    {nama: '', nik: '', phone: '', relation: ''},
  ])

  const [agreements, setAgreements] = useState({
    verifikasi: false,
    pernyataanBenar: false,
    setujuKetentuan: false,
  })

  useEffect(() => {
    setPassengers((prev) => {
      const newPassengers = [...prev]
      newPassengers[0] = {
        ...newPassengers[0],
        nama: formPendaftar.nama || '',
        nik: formPendaftar.nik || '',
        phone: formPendaftar.hp || '',
        relation: 'DIRI SENDIRI',
      }
      return newPassengers
    })
  }, [formPendaftar.nama, formPendaftar.nik, formPendaftar.hp])

  useEffect(() => {
    if (!state || !state.origin || state.origin === '-') {
      Swal.fire({
        icon: 'warning',
        title: 'Sesi Berakhir',
        text: 'Kamu harus pilih rute dulu',
        confirmButtonColor: '#72BF44',
        allowOutsideClick: false,
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/mudik-bersama/pegadaian/reservasi', {replace: true})
        }
      })
    }
  }, [state, navigate])

  const origin = state?.origin || '-'
  const originAddress = state?.originAddress || '-'
  const schDate = state?.schDate || '-'
  const sta = state?.sta || '-'
  const destination = state?.destinationSelectedParams || '-'

  const isSubmitDisabled =
    !agreements.verifikasi ||
    !agreements.pernyataanBenar ||
    !agreements.setujuKetentuan ||
    !captchaValue ||
    !ktpFile ||
    !formPendaftar.nama ||
    !formPendaftar.jenisKelamin ||
    !formPendaftar.tanggalLahir ||
    !formPendaftar.alamatLengkap ||
    !formPendaftar.hp ||
    formPendaftar.nik.length !== 16 ||
    !formPendaftar.noKK ||
    (isNasabah && !formPendaftar.nomorCif) ||
    passengers
      .slice(0, jumlahPeserta)
      .some((p) => !p.nama || !p.nik || p.nik.length !== 16 || !p.relation)

  const handlePassengerChange = (index: number, field: string, value: string) => {
    setPassengers((prev) => {
      const updated = [...prev]
      updated[index] = {...updated[index], [field]: value}
      return updated
    })
  }

  if (!state || !state.origin || state.origin === '-') {
    return (
      <div className='d-flex justify-content-center align-items-center vh-100'>
        <div className='spinner-border text-success' role='status'></div>
      </div>
    )
  }

  const handleSubmit = async () => {
    if (captchaValue !== captcha) {
      Swal.fire({icon: 'error', title: 'Captcha tidak valid', confirmButtonColor: '#72BF44'})
      setCaptcha(generateCaptcha())
      return
    }

    const {nik, noKK, nama, jenisKelamin, tanggalLahir, hp, nomorCif, alamatLengkap} = formPendaftar

    if (!nama || !nik || !noKK || !jenisKelamin || !tanggalLahir || !hp || !alamatLengkap) {
      Swal.fire({
        icon: 'warning',
        title: 'Data Belum Lengkap',
        text: 'Semua kolom bertanda bintang (*) pada pendaftar wajib diisi.',
        confirmButtonColor: '#72BF44',
      })
      return
    }

    if (nik.length !== 16 || noKK.length !== 16) {
      Swal.fire({
        icon: 'error',
        title: 'Format NIK/KK Salah',
        text: 'NIK dan Nomor KK harus tepat 16 digit.',
        confirmButtonColor: '#72BF44',
      })
      return
    }

    if (isNasabah && !nomorCif) {
      Swal.fire({
        icon: 'warning',
        title: 'Nomor CIF Wajib',
        text: 'Silakan masukkan nomor CIF Anda sebagai Nasabah.',
        confirmButtonColor: '#72BF44',
      })
      return
    }

    const dataPenumpangYangDiisi = passengers.slice(0, jumlahPeserta)
    const isPassengerValid = dataPenumpangYangDiisi.every((p) => {
      return p.nama.trim() !== '' && p.nik.length === 16 && p.relation !== ''
    })

    if (!isPassengerValid) {
      Swal.fire({
        icon: 'warning',
        title: 'Data Penumpang Belum Lengkap',
        text: `Mohon lengkapi Nama, NIK (16 digit), dan Hubungan Keluarga untuk ${jumlahPeserta} peserta.`,
        confirmButtonColor: '#72BF44',
      })
      return
    }

    // 4. Validasi File KTP
    if (!ktpFile) {
      Swal.fire({
        icon: 'warning',
        title: 'Upload KTP',
        text: 'Silakan upload foto KTP Anda terlebih dahulu.',
        confirmButtonColor: '#72BF44',
      })
      return
    }

    const formData = new FormData()
    formData.append('routeId', state.id)
    formData.append('origin', state.origin)
    formData.append('destination', state.destinationSelectedParams)
    formData.append('nik', nik)
    formData.append('noKK', noKK)
    formData.append('nama', nama)
    formData.append('jenisKelamin', jenisKelamin)
    formData.append('tanggalLahir', tanggalLahir)
    formData.append('alamatLengkap', formPendaftar.alamatLengkap)
    formData.append('hp', hp)
    formData.append('partnerName', 'Pegadaian')
    formData.append('nomorCif', isNasabah ? nomorCif : '')

    passengers.forEach((p, index) => {
      if (p.nama && p.nik) {
        formData.append(`passengers[${index}].nama`, p.nama)
        formData.append(`passengers[${index}].nik`, p.nik)
        formData.append(`passengers[${index}].phone`, p.phone || '')
        formData.append(`passengers[${index}].relation`, p.relation || '')
      }
    })

    if (ktpFile) {
      formData.append('passengers[0].ktpFile', ktpFile)
    }

    try {
      // Tampilkan Loading (Opsional tapi bagus untuk UX)
      Swal.showLoading()

      const response = await axiosInstance.post('/rsvm/api/rsvm-booking/v1/booking', formData, {
        headers: {'Content-Type': 'multipart/form-data'},
      })

      if (response.status === 200 || response.status === 201) {
        Swal.fire({
          icon: 'success',
          title: 'Berhasil',
          text: 'Pendaftaran Anda telah berhasil disubmit!',
          confirmButtonColor: '#72BF44',
        }).then(() => {
          navigate('/mudik-bersama/pegadaian/reservasi')
        })
      }
    } catch (error: any) {
      let message = 'Terjadi kesalahan sistem.'
      if (error.response && error.response.data) {
        message = error.response.data.message || message
      }

      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: message,
        confirmButtonColor: '#72BF44',
      })
    }
  }

  const handleNikChange = (e) => {
    const value = e.target.value.replace(/\D/g, '')
    if (value.length <= 16) {
      setFormPendaftar({...formPendaftar, nik: value})
    }
  }

  const validateNik = () => {
    if (formPendaftar.nik.length > 0 && formPendaftar.nik.length < 16) {
      Swal.fire({
        icon: 'error',
        title: 'NIK Tidak Valid',
        text: 'NIK harus berjumlah 16 digit!',
        confirmButtonColor: '#72BF44',
      })
    }
  }

  // --- 5. RENDER UI ---
  return (
    <>
      {showQuotaModal && (
        <div
          className='position-absolute w-100 h-100 d-flex align-items-center justify-content-center px-4'
          style={{
            zIndex: 999,
            backgroundColor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            top: 0,
            left: 0,
            borderRadius: 'inherit',
          }}
        >
          <div
            className='bg-white p-5 text-center shadow-lg d-flex flex-column'
            style={{
              borderRadius: '24px',
              maxWidth: '380px',
              height: '500px', // Sesuai permintaan
              border: '1px solid #72BF44',
            }}
          >
            {/* Konten Atas */}
            <div className='mt-4'>
              <img
                src={toAbsoluteUrl('/img/icon-bus.png')}
                alt='Bus'
                style={{
                  width: 'clamp(120px, 28vw, 180px)',
                  marginBottom: '2rem',
                }}
              />
              <h3 className='fw-bolder mb-3' style={{color: '#333', fontSize: '1.5rem'}}>
                Mohon maaf kuota penuh !
              </h3>
              <p className='text-muted fs-7 px-2'>
                Terima kasih atas antusiasme Anda. Mohon maaf, saat ini{' '}
                <span className='fw-bold text-dark'>
                  kuota Mudik Bersama Pegadaian telah terpenuhi
                </span>
                .
              </p>
            </div>

            {/* Button di Paling Bawah */}
            <div className='mt-auto'>
              <button
                className='btn w-100 py-3 fw-bold text-white'
                style={{
                  backgroundColor: '#72BF44',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(114, 191, 68, 0.3)',
                }}
                onClick={() => setShowQuotaModal(false)}
              >
                Saya Mengerti
              </button>
            </div>
          </div>
        </div>
      )}
      <div className='container-xxl p-0'>
        <div className='row justify-content-center mx-0'>
          <div
            className='col-12 col-md-8 col-lg-5 p-0 bg-white pb-md-10 pb-8 mx-auto'
            style={{position: 'relative'}}
          >
            <div
              className='position-relative w-100 overflow-hidden'
              style={{borderEndEndRadius: 20, borderEndStartRadius: 20}}
            >
              <img
                src={toAbsoluteUrl('/img/pegadaian-bg.png')}
                alt='Background'
                className='w-100 d-block'
                style={{
                  height: 'clamp(190px, 24vw, 230px)',
                  objectFit: 'cover',
                }}
              />

              <button
                onClick={() => navigate(-1)}
                className='btn position-absolute top-0 start-0 m-4 z-index-1'
              >
                <i className='bi bi-x-lg fs-2 text-white'></i>
              </button>

              {/* Bus Illustration */}
              <img
                src={toAbsoluteUrl('/img/icon-bus.png')}
                alt='Bus'
                className='position-absolute'
                style={{
                  width: 'clamp(120px, 28vw, 180px)',
                  left: 'clamp(12px, 4vw, 25px)',
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              />

              {/* Title Text */}
              <div
                className='position-absolute text-end'
                style={{
                  right: 'clamp(16px, 4vw, 40px)',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  maxWidth: '65%',
                }}
              >
                <img
                  src={toAbsoluteUrl('/img/pegadaian-logo.png')}
                  alt='Pegadaian'
                  style={{
                    height: 'clamp(16px, 4vw, 24px)',
                    objectFit: 'contain',
                  }}
                />
                <h2 className='fw-bolder mb-0 fs-3 fs-md-2 fs-lg-1'>
                  <span style={{color: '#72BF44'}}>Mudik</span>{' '}
                  <span style={{color: '#BFD730'}}>Bersama</span>
                </h2>
                <p className='text-white small fw-bold mb-0'>Program Mudik Bersama Pegadaian</p>
              </div>
            </div>

            {/* Alert Info Box */}
            <div className='mx-4 mt-n8 position-relative z-index-2'>
              <div
                className='alert alert-warning d-flex align-items-center p-4 shadow-sm'
                style={{backgroundColor: '#FFF4D6', border: 'none', borderRadius: '12px'}}
              >
                <i className='bi bi-exclamation-triangle-fill fs-2 text-warning me-3'></i>
                <div className='small text-dark'>
                  Silahkan lengkapi data dibawah ini dengan benar. Data akan diverifikasi.
                </div>
              </div>
            </div>

            <div className='px-5 mt-8'>
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <span className='text-muted small'>Kota Asal</span>
                <span className='text-muted small'>Kota Tujuan</span>
              </div>

              <div className='d-flex align-items-center mb-6'>
                {/* Origin */}
                <h4 className='fw-bolder mb-0 me-2'>{origin}</h4>
                <div className='d-flex align-items-center'>
                  <i className='bi bi-circle-fill text-success' style={{fontSize: '8px'}}></i>
                </div>

                {/* Garis Putus-putus */}
                <div
                  className='flex-grow-1 mx-3 border-bottom border-success'
                  style={{height: '2px', borderStyle: 'dotted'}}
                ></div>

                {/* Destination */}
                <div className='d-flex align-items-center me-2'>
                  <i className='bi bi-circle-fill text-success' style={{fontSize: '8px'}}></i>
                </div>
                <h4 className='fw-bolder mb-0 me-2'>{destination}</h4>
              </div>

              {/* Detail Lokasi & Jam */}
              <div className='mb-4'>
                <label className='fw-bold d-block mb-1'>Lokasi Keberangkatan :</label>
                <p className='text-muted small mb-0'>{originAddress}</p>
              </div>

              <div className='mb-8'>
                <label className='fw-bold d-block mb-1'>Jam Keberangkatan :</label>
                <span className='fw-bold'>
                  {new Date(schDate).toLocaleDateString('id-ID', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
                <p className='text-dark fw-bold mb-0'>{sta}</p>
              </div>

              <div className='mt-8'>
                {/* Header Form Biru */}
                <div
                  className='p-3 d-flex justify-content-between align-items-center cursor-pointer'
                  style={{backgroundColor: '#037556'}}
                  onClick={() => setOpenDataDiri(!openDataDiri)}
                >
                  <span className='text-white fw-bolder ms-2'>A. DATA DIRI PENDAFTAR</span>

                  <i
                    className={`bi bi-chevron-down text-white me-2 transition ${
                      openDataDiri ? 'rotate-180' : ''
                    }`}
                  ></i>
                </div>

                <div
                  className='overflow-hidden transition'
                  style={{
                    maxHeight: openDataDiri ? '2000px' : '0px',
                    marginBottom: openDataDiri ? '20px' : '30px',
                    opacity: openDataDiri ? 1 : 0,
                  }}
                >
                  <div className='px-5 py-6'>
                    {/* Apakah Nasabah */}
                    <div className='mb-5'>
                      <label className='fw-bold mb-2 d-block'>Apakah Kamu Nasabah Pegadaian?</label>

                      <div className='d-flex gap-5 mt-2'>
                        <div className='form-check'>
                          <input
                            className='form-check-input'
                            type='radio'
                            name='isNasabah'
                            id='nasabah_tidak'
                            checked={!isNasabah}
                            onChange={() => setIsNasabah(false)}
                          />
                          <label className='form-check-label' htmlFor='nasabah_tidak'>
                            Tidak
                          </label>
                        </div>

                        <div className='form-check'>
                          <input
                            className='form-check-input'
                            type='radio'
                            name='isNasabah'
                            id='nasabah_ya'
                            checked={isNasabah}
                            onChange={() => setIsNasabah(true)}
                          />
                          <label className='form-check-label' htmlFor='nasabah_ya'>
                            Ya
                          </label>
                        </div>
                      </div>
                    </div>
                    {isNasabah && (
                      <div className='mb-5'>
                        <label className='fw-bold mb-2'>
                          Nomor CIF <span className='text-danger'>*</span>
                        </label>
                        <input
                          type='text'
                          required={isNasabah}
                          className='form-control form-control-solid p-3'
                          placeholder='Masukkan Nomor CIF'
                          value={formPendaftar.nomorCif}
                          onChange={(e) =>
                            setFormPendaftar({...formPendaftar, nomorCif: e.target.value})
                          }
                          style={{backgroundColor: '#F5F8FA'}}
                        />
                      </div>
                    )}

                    {/* Nama Lengkap */}
                    <div className='mb-5'>
                      <label className='fw-bold mb-2'>
                        Nama Lengkap <span className='text-danger'>*</span>
                      </label>
                      <input
                        type='text'
                        className='form-control form-control-solid p-3'
                        placeholder='Masukan Nama Sesuai KTP'
                        value={formPendaftar.nama}
                        onChange={(e) => setFormPendaftar({...formPendaftar, nama: e.target.value})}
                        style={{backgroundColor: '#F5F8FA'}}
                      />
                    </div>

                    {/* NIK Row */}
                    <div className='mb-5'>
                      <label className='fw-bold mb-2'>
                        NIK (16 Digit) <span className='text-danger'>*</span>
                      </label>
                      <input
                        type='text'
                        required
                        className='form-control form-control-solid p-3'
                        placeholder='Masukan 16 digit NIK'
                        value={formPendaftar.nik}
                        onChange={handleNikChange}
                        onBlur={validateNik} // Validasi saat user selesai mengetik/pindah input
                        style={{backgroundColor: '#F5F8FA', borderRadius: '10px'}}
                      />
                      {formPendaftar.nik.length > 0 && formPendaftar.nik.length < 16 && (
                        <small className='text-danger mt-1 d-block'>Minimal 16 digit</small>
                      )}
                    </div>

                    <div className='mb-5'>
                      <label className='fw-bold mb-2'>
                        NIK Kartu Keluarga <span className='text-danger'>*</span>
                      </label>
                      <input
                        type='text'
                        required
                        className='form-control form-control-solid p-3'
                        placeholder='Masukan Kartu Keluarga'
                        value={formPendaftar.noKK}
                        onChange={(e) => setFormPendaftar({...formPendaftar, noKK: e.target.value})}
                        style={{backgroundColor: '#F5F8FA'}}
                      />
                    </div>

                    {/* Jenis Kelamin */}
                    <div className='mb-5'>
                      <label className='fw-bold mb-2 d-block'>
                        Jenis Kelamin <span className='text-danger'>*</span>
                      </label>
                      <div className='d-flex gap-5 mt-2'>
                        {/* Opsi Laki-laki */}
                        <div className='form-check'>
                          <input
                            className='form-check-input'
                            type='radio'
                            name='gender'
                            id='laki'
                            checked={formPendaftar.jenisKelamin === 'L'}
                            onChange={() => setFormPendaftar({...formPendaftar, jenisKelamin: 'L'})}
                          />
                          <label className='form-check-label text-muted' htmlFor='laki'>
                            Laki-laki
                          </label>
                        </div>

                        {/* Opsi Perempuan */}
                        <div className='form-check'>
                          <input
                            className='form-check-input'
                            type='radio'
                            name='gender'
                            id='perempuan'
                            checked={formPendaftar.jenisKelamin === 'P'}
                            onChange={() => setFormPendaftar({...formPendaftar, jenisKelamin: 'P'})}
                          />
                          <label className='form-check-label text-muted' htmlFor='perempuan'>
                            Perempuan
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Tanggal Lahir */}
                    <div className='mb-5'>
                      <label className='fw-bold mb-2'>
                        Tanggal Lahir <span className='text-danger'>*</span>
                      </label>
                      <div className='position-relative'>
                        <input
                          type='date'
                          required
                          className='form-control form-control-solid p-3 cursor-pointer'
                          style={{backgroundColor: '#F5F8FA'}}
                          max='2009-01-01'
                          value={formPendaftar.tanggalLahir}
                          onChange={(e) => {
                            const val = e.target.value
                            setFormPendaftar({...formPendaftar, tanggalLahir: val})
                          }}
                        />
                      </div>
                    </div>

                    {/* Alamat */}
                    <div className='mb-5'>
                      <label className='fw-bold mb-2'>Alamat Lengkap</label>
                      <textarea
                        required
                        className='form-control form-control-solid p-3'
                        rows={2}
                        style={{backgroundColor: '#F5F8FA'}}
                        value={formPendaftar.alamatLengkap}
                        onChange={(e) =>
                          setFormPendaftar({...formPendaftar, alamatLengkap: e.target.value})
                        }
                      ></textarea>
                    </div>

                    {/* Nomor HP */}
                    <div className='mb-5'>
                      <label className='fw-bold mb-2'>
                        Nomor HP (WhatsApp Aktif) <span className='text-danger'>*</span>
                      </label>
                      <input
                        type='text'
                        required
                        className='form-control form-control-solid p-3'
                        placeholder='08 xxxxxxxxxxxx'
                        style={{backgroundColor: '#F5F8FA'}}
                        value={formPendaftar.hp}
                        onChange={(e) => setFormPendaftar({...formPendaftar, hp: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className='mt-8'>
                {/* Header Accordion */}
                <div
                  className='p-3 d-flex justify-content-between align-items-center cursor-pointer'
                  style={{backgroundColor: '#037556'}}
                  onClick={() => setOpenDataMudik(!openDataMudik)}
                >
                  <span className='text-white fw-bolder ms-2'>B. DATA PESERTA MUDIK</span>
                  <i
                    className={`bi bi-chevron-down text-white me-2 transition ${
                      openDataMudik ? 'rotate-180' : ''
                    }`}
                  ></i>
                </div>

                <div
                  className='overflow-hidden transition px-5 py-6'
                  style={{
                    maxHeight: openDataMudik ? '3000px' : '0px',
                    opacity: openDataMudik ? 1 : 0,
                    transition: 'all 0.4s ease-in-out',
                  }}
                >
                  {/* Pilihan Jumlah Peserta */}
                  <div className='mb-8'>
                    <label className='fw-bold mb-3 d-block'>
                      Jumlah Peserta <span className='text-danger'>*</span>
                    </label>
                    <div className='d-flex gap-8'>
                      {[1, 2, 3].map((num) => (
                        <div className='form-check' key={num}>
                          <input
                            className='form-check-input'
                            type='radio'
                            name='jmlPeserta'
                            id={`peserta${num}`}
                            checked={jumlahPeserta === num}
                            onChange={() => setJumlahPeserta(num)}
                          />
                          <label
                            className='form-check-label fw-bold text-muted'
                            htmlFor={`peserta${num}`}
                          >
                            {num} Orang
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {[...Array(jumlahPeserta)].map((_, index) => {
                    const isPendaftar = index === 0

                    return (
                      <div key={index}>
                        {/* Sub-Header Divider */}
                        <div className='d-flex align-items-center mb-5 mt-2'>
                          <span
                            className='fw-bolder text-success text-uppercase me-3'
                            style={{fontSize: '12px', letterSpacing: '1px'}}
                          >
                            {isPendaftar
                              ? 'Data Peserta Mudik (Pendaftar)'
                              : `Data Peserta Tambahan ${index}`}
                          </span>
                          <div
                            className='flex-grow-1'
                            style={{height: '1px', backgroundColor: '#eee'}}
                          ></div>
                        </div>

                        {/* Input Nama Peserta */}
                        <div className='mb-4'>
                          <label className='small fw-bold mb-2'>
                            Nama Peserta <span className='text-danger'>*</span>
                          </label>
                          <input
                            type='text'
                            required
                            className={`form-control ${
                              isPendaftar ? 'bg-secondary opacity-75' : 'bg-light'
                            } border-0 p-3`}
                            placeholder='Nama sesuai KTP'
                            // Perbaikan: Jika pendaftar, ambil langsung dari formPendaftar
                            value={
                              isPendaftar ? formPendaftar.nama || '' : passengers[index]?.nama || ''
                            }
                            readOnly={isPendaftar}
                            onChange={(e) =>
                              !isPendaftar && handlePassengerChange(index, 'nama', e.target.value)
                            }
                            style={{borderRadius: '10px'}}
                          />
                        </div>

                        {/* Input NIK */}
                        <div className='mb-4'>
                          <label className='small fw-bold mb-2'>
                            NIK <span className='text-danger'>*</span>
                          </label>
                          <input
                            type='text'
                            required
                            className={`form-control ${
                              isPendaftar ? 'bg-secondary opacity-75' : 'bg-light'
                            } border-0 p-3`}
                            placeholder='NIK 16 digit'
                            value={
                              isPendaftar ? formPendaftar.nik || '' : passengers[index]?.nik || ''
                            }
                            readOnly={isPendaftar}
                            onChange={(e) =>
                              !isPendaftar && handlePassengerChange(index, 'nik', e.target.value)
                            }
                            style={{borderRadius: '10px'}}
                          />
                        </div>

                        {!isPendaftar && (
                          <div className='mb-4'>
                            <label className='small fw-bold mb-2'>
                              Hubungan Keluarga <span className='text-danger'>*</span>
                            </label>
                            <select
                              className='form-select bg-light border-0 p-3'
                              style={{borderRadius: '10px'}}
                              required
                              value={passengers[index]?.relation || ''}
                              onChange={(e) =>
                                handlePassengerChange(index, 'relation', e.target.value)
                              }
                            >
                              <option value=''>Pilih Hubungan</option>
                              <option value='ISTRI'>Istri</option>
                              <option value='SUAMI'>Suami</option>
                              <option value='ANAK'>Anak</option>
                            </select>
                          </div>
                        )}

                        {/* Input Nomor HP */}
                        <div className='mb-2'>
                          <label className='small fw-bold mb-2'>
                            Nomor Hp <span className='text-danger'>*</span>
                          </label>
                          <input
                            type='text'
                            required
                            className={`form-control ${
                              isPendaftar ? 'bg-secondary opacity-75' : 'bg-light'
                            } border-0 p-3`}
                            placeholder='Nomor yang terdaftar (Whatsapp)'
                            value={
                              isPendaftar ? formPendaftar.hp || '' : passengers[index]?.phone || ''
                            }
                            readOnly={isPendaftar}
                            onChange={(e) =>
                              !isPendaftar && handlePassengerChange(index, 'phone', e.target.value)
                            }
                            style={{borderRadius: '10px'}}
                          />

                          {/* Note Khusus untuk Anak atau Peserta Tambahan */}
                          {!isPendaftar && (
                            <div className='mt-2 ms-1'>
                              <span
                                className='text-muted'
                                style={{fontSize: '11px', fontStyle: 'italic'}}
                              >
                                {passengers[index]?.relation === 'ANAK'
                                  ? '* Nomor telepon anak boleh diisi sama dengan nomor orang tua'
                                  : '* Pastikan nomor WhatsApp aktif untuk koordinasi'}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Spacer antar peserta */}
                        {index < jumlahPeserta - 1 && <div className='mb-10'></div>}
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className='mb-8'>
                <div
                  className='p-3 d-flex justify-content-between align-items-center cursor-pointer'
                  style={{backgroundColor: '#037556'}}
                  onClick={() => setOpenUpload(!openUpload)}
                >
                  <span className='text-white fw-bolder ms-2'>C. UPLOAD DATA</span>
                  <i
                    className={`bi bi-chevron-down text-white me-2 transition ${
                      openUpload ? 'rotate-180' : ''
                    }`}
                  ></i>
                </div>

                <div
                  className='overflow-hidden transition px-5 py-6'
                  style={{
                    maxHeight: openUpload ? '2000px' : '0px',
                    opacity: openUpload ? 1 : 0,
                  }}
                >
                  {/* Upload KTP */}
                  <div className='mb-6'>
                    <label className='fw-bold mb-2 d-block'>
                      Upload KTP <span className='text-danger'>*</span>
                    </label>
                    <div
                      className='border border-success border-2 border-dotted d-flex flex-column align-items-center justify-content-center py-6 position-relative'
                      style={{
                        borderStyle: 'dashed !important',
                        borderRadius: '12px',
                        backgroundColor: '#f8fbff',
                      }}
                    >
                      <input
                        type='file'
                        required
                        className='position-absolute opacity-0 w-100 h-100 cursor-pointer'
                        accept='.pdf'
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            setKtpFile(file)
                            setKtpFileName(file.name)
                          }
                        }}
                      />
                      <span className='text-success fw-bold fs-4 mb-1'>
                        {ktpFileName || 'Pilih File KTP'}
                      </span>
                    </div>
                    <div className='mt-2 text-muted' style={{fontSize: '11px'}}>
                      Pastikan scan KTP terlihat jelas dan terbaca dengan format (.pdf)
                      <br />
                      Mohon scan KTP dijadikan satu dengan peserta tambahan.
                    </div>
                  </div>

                  {/* Captcha Section */}
                  {/* Captcha Section */}
                  <div className='mb-8'>
                    <div className='row align-items-center g-3'>
                      <div className='col-7'>
                        <input
                          type='text'
                          required
                          className='form-control p-3 bg-light border-0'
                          placeholder='Masukkan captcha'
                          style={{borderRadius: '10px'}}
                          value={captchaValue}
                          onChange={(e) => setCaptchaValue(e.target.value)}
                        />
                      </div>

                      <div className='col-5 d-flex align-items-center gap-2'>
                        {/* Captcha Number */}
                        <div
                          className='p-2 rounded flex-grow-1 text-center'
                          style={{
                            fontWeight: 'bold',
                            letterSpacing: '4px',
                            fontSize: '18px',
                            background: 'linear-gradient(45deg, #eee, #ccc)',
                            userSelect: 'none',
                          }}
                        >
                          {captcha}
                        </div>

                        {/* Refresh */}
                        <button
                          type='button'
                          className='btn btn-sm btn-icon btn-light-success'
                          onClick={() => {
                            setCaptcha(generateCaptcha())
                            setCaptchaValue('')
                          }}
                        >
                          <i className='bi bi-arrow-clockwise fs-3'></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Triple Agreement Checkbox */}
                  <div className='d-flex flex-column gap-4 mb-10'>
                    <div className='form-check'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='check1'
                        checked={agreements.verifikasi}
                        onChange={(e) =>
                          setAgreements({...agreements, verifikasi: e.target.checked})
                        }
                      />
                      <label className='form-check-label small fw-bold text-muted' htmlFor='check1'>
                        VERIFIKASI DAN SUBMIT
                      </label>
                    </div>

                    <div className='form-check'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='check2'
                        checked={agreements.pernyataanBenar}
                        onChange={(e) =>
                          setAgreements({...agreements, pernyataanBenar: e.target.checked})
                        }
                      />
                      <label className='form-check-label small fw-bold text-muted' htmlFor='check2'>
                        Saya menyatakan bahwa data yang diunggah adalah benar.
                      </label>
                    </div>

                    <div className='form-check'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='check3'
                        checked={agreements.setujuKetentuan}
                        onChange={(e) =>
                          setAgreements({...agreements, setujuKetentuan: e.target.checked})
                        }
                      />
                      <label className='form-check-label small fw-bold text-muted' htmlFor='check3'>
                        Dengan ini saya menyetujui dan bersedia mengikuti seluruh ketentuan Program
                        Mudik Bareng Pegadaian
                      </label>
                    </div>
                  </div>

                  {/* Button Submit dengan Logic Disabled */}
                </div>
              </div>

              <button
                className='btn btn-success w-100 py-4 fw-bold shadow mt-5'
                // Button disabled jika state submit disabled ATAU jika kuota penuh
                disabled={isSubmitDisabled || isQuotaFull}
                onClick={handleSubmit}
                style={{
                  // Warna abu-abu jika disabled atau kuota penuh
                  backgroundColor: isSubmitDisabled || isQuotaFull ? '#E0E0E0' : '#72BF44',
                  color: isSubmitDisabled || isQuotaFull ? '#999' : '#fff',
                  borderRadius: '40px',
                  border: 'none',
                  transition: 'all 0.3s ease',
                  cursor: isQuotaFull ? 'not-allowed' : 'pointer',
                }}
              >
                {/* Kondisi teks bertingkat */}
                {isQuotaFull
                  ? 'Pemesanan Ditutup'
                  : captchaValue !== captcha && captchaValue.length === captcha.length
                  ? 'Captcha Salah'
                  : 'Lanjutkan Pemesanan'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default PegadaianCheckoutPage
